import { BalanceData } from "../data/balance";
import { NO_VIP_BENEFITS, VipBenefits } from "../data/vip";
import { Fix64 } from "../maths/fix64";
import { GearBonus, NO_GEAR_BONUS } from "./gear";
import { withVipCombat } from "./vipLevels";

/**
 * The fair-mode switch (MP-32, docs/MODES.md §4): what a run is built from. A campaign run takes the player's save —
 * the towers they own, their tower and Fortress levels, and `PlayerProfile.gearBonuses()` (gear + VIP combat + Gold
 * Research: the ONE way those reach the sim, docs/SHOP.md §8). A `normalised` mode (Daily Road now; challenge codes,
 * Race Week, Expedition later) takes this profile from data instead: the same towers at the same level for every
 * player and no gear or research bonus, so no gear, no Gold Research and no paid consumables can reach it, whatever the
 * save holds. VIP combat (owner D1, 2026-10-12: paid power counts everywhere, a VIP just has it easier) joins on top
 * when the data says `allowVip`, through the same `withVipCombat` the campaign uses.
 */
export interface RunProfile {
  /** Per tower archetype: owned (dealt by the draft). */
  readonly owned: readonly boolean[];
  /** Per tower archetype (the Fortress at its own index): its level for the run. */
  readonly towerLevels: readonly number[];
  /** Per tower archetype: what gear, VIP and research add (a normalised run: VIP combat at most). */
  readonly gear: readonly GearBonus[];
}

/**
 * The normalised profile (modes.json `normalised`): its towers owned, every level `towerLevel`, no gear and no
 * research; with `allowVip`, the player's VIP combat bonus `vip` on top (nothing at VIP 0, so a VIP-0 run is the
 * data's profile exactly).
 */
export function normalisedProfile(balance: BalanceData, vip: VipBenefits = NO_VIP_BENEFITS): RunProfile {
  const d = balance.modes.normalised;
  const none = balance.towers.map(() => NO_GEAR_BONUS);
  return {
    owned: balance.towers.map((t) => !t.isBase && d.towers.includes(t.id)),
    towerLevels: balance.towers.map(() => d.towerLevel),
    gear: d.allowVip ? withVipCombat(balance, none, vip) : none,
  };
}

const sameFix = (a: Fix64 | undefined, b: Fix64 | undefined): boolean => (a ?? Fix64.Zero).eq(b ?? Fix64.Zero);

function sameBonus(a: GearBonus, b: GearBonus): boolean {
  return sameFix(a.damage, b.damage) && sameFix(a.range, b.range) && sameFix(a.fireRate, b.fireRate) && sameFix(a.hp, b.hp) && sameFix(a.points, b.points)
    && sameFix(a.troops, b.troops) && a.mythic.length === 0 && b.mythic.length === 0 && !a.setBonus && !b.setBonus;
}

/**
 * Whether a profile is the normalised one for a player of VIP benefits `vip` (a test, and a guard before a fair run's
 * score is kept): the data's towers and levels, and no bonus beyond what `allowVip` lets VIP add.
 */
export function isNormalisedProfile(balance: BalanceData, p: RunProfile, vip: VipBenefits = NO_VIP_BENEFITS): boolean {
  const n = normalisedProfile(balance, vip);
  return n.owned.every((o, i) => p.owned[i] === o) && n.towerLevels.every((l, i) => p.towerLevels[i] === l)
    && balance.towers.every((_, i) => sameBonus(p.gear[i] ?? NO_GEAR_BONUS, n.gear[i]));
}

/** A profile's power score as the HUD shows it (PlayerProfile.powerScore's weights; no gear score: a fair run has none). */
export function profilePower(balance: BalanceData, p: RunProfile): number {
  const base = balance.towers.findIndex((t) => t.isBase);
  let sum = (base >= 0 ? p.towerLevels[base] : 1) * balance.meta.powerBaseWeight;
  for (const l of p.towerLevels) sum += l * balance.meta.powerTowerWeight;
  return sum;
}
